import { z } from "zod";
import { DealPaymentsSchema, type DealPayments } from "@/lib/deals/types";

// Payment checkbox keys
export const PAYMENT_CHECKBOX_KEYS = [
  "buyer_payment_received",
  "buyer_commission_received",
  "seller_payment_sent",
  "admin_commission_received",
] as const;

export type PaymentCheckboxKey = (typeof PAYMENT_CHECKBOX_KEYS)[number];

// Which checkboxes each user role can toggle
export const USER_PAYMENT_CHECKBOX_KEYS: Record<"buyer" | "seller", readonly PaymentCheckboxKey[]> =
  {
    buyer: ["buyer_payment_received", "buyer_commission_received"],
    seller: ["seller_payment_sent"],
  };

// Labels for payment checkboxes (German)
export const PAYMENT_CHECKBOX_LABELS: Record<PaymentCheckboxKey, string> = {
  buyer_payment_received: "Zahlung aus Kaufvertrag angewiesen",
  buyer_commission_received: "Zahlung aus Provisionsvereinbarung angewiesen",
  seller_payment_sent: "Verkäufer hat Zahlung erhalten",
  admin_commission_received: "Provision eingegangen",
};

// Descriptions for payment checkboxes (German)
export const PAYMENT_CHECKBOX_DESCRIPTIONS: Record<PaymentCheckboxKey, string> = {
  buyer_payment_received:
    "Der Käufer hat die Zahlung aus dem Kaufvertrag per Banküberweisung angewiesen.",
  buyer_commission_received:
    "Der Käufer hat die Zahlung aus der Provisionsvereinbarung angewiesen.",
  seller_payment_sent: "Der Verkäufer hat die Zahlung per Banküberweisung erhalten.",
  admin_commission_received: "Die Plattform-Provision wurde auf unser Konto überwiesen.",
};

// Input schema for updating a single payment checkbox
export const UpdatePaymentCheckboxInputSchema = z.object({
  checkbox: z.enum(PAYMENT_CHECKBOX_KEYS),
  checked: z.boolean(),
});

export type UpdatePaymentCheckboxInput = z.infer<typeof UpdatePaymentCheckboxInputSchema>;

// Input schema for setting payment amounts
export const SetPaymentAmountsInputSchema = z.object({
  total_amount: z.number().positive("Gesamtbetrag muss positiv sein"),
  buyer_commission_amount: z.number().nonnegative("Provision muss >= 0 sein").optional(),
  seller_payout_amount: z.number().positive("Auszahlungsbetrag muss positiv sein").optional(),
  admin_commission_amount: z.number().nonnegative("Provision muss >= 0 sein").optional(),
});

export type SetPaymentAmountsInput = z.infer<typeof SetPaymentAmountsInputSchema>;

// Input schema for adding payment notes
export const UpdatePaymentNotesInputSchema = z.object({
  bank_reference: z.string().optional(),
  payment_notes: z.string().optional(),
});

export type UpdatePaymentNotesInput = z.infer<typeof UpdatePaymentNotesInputSchema>;

// Helper to check if all payments are complete
export function areAllPaymentsComplete(payment: DealPayments | null): boolean {
  if (!payment) return false;
  return PAYMENT_CHECKBOX_KEYS.every((key) => payment[key] === true);
}

/**
 * When the payments were complete: the latest of the four confirmations, or null while one is
 * missing. A confirmation taken back and given again makes a new completion.
 */
export function paymentsCompletedAt(payment: DealPayments | null): string | null {
  if (!areAllPaymentsComplete(payment)) return null;
  const times = PAYMENT_CHECKBOX_KEYS.map((key) => payment?.[`${key}_at`] ?? null);
  if (times.some((time) => time === null)) return null;
  return times.reduce((latest, time) => (Date.parse(time!) > Date.parse(latest!) ? time : latest))!;
}

// Helper to get completion count
export function getPaymentCompletionCount(payment: DealPayments | null): number {
  if (!payment) return 0;
  return PAYMENT_CHECKBOX_KEYS.filter((key) => payment[key]).length;
}

// Helper to get payment status summary
export interface PaymentCheckboxStatus {
  key: PaymentCheckboxKey;
  label: string;
  description: string;
  checked: boolean;
  checkedAt: string | null;
  checkedBy: string | null;
}

export function getPaymentCheckboxStatuses(payment: DealPayments | null): PaymentCheckboxStatus[] {
  return PAYMENT_CHECKBOX_KEYS.map((key) => ({
    key,
    label: PAYMENT_CHECKBOX_LABELS[key],
    description: PAYMENT_CHECKBOX_DESCRIPTIONS[key],
    checked: payment?.[key] ?? false,
    checkedAt: payment?.[`${key}_at` as keyof DealPayments] as string | null,
    checkedBy: payment?.[`${key}_by` as keyof DealPayments] as string | null,
  }));
}

// Re-export for convenience
export type { DealPayments };
export { DealPaymentsSchema };
